import https from "@/services/";
import Vue from 'vue';

let verrou = false, _retry = 0;


// Helper pour vérifier si un utilisateur appartient à un groupe de rôles
export const isInRoleGroup = (userRole, group) => {
    const roleGroups = {
        admin: ['ADMIN'],
        administratif: ['ADMINISTRATIF'],
        comite: ['ROLE_COMITE'],
        expert: ['EXPERT_COMPTABLE_ENTITE', 'COMPTABLE_AGREE', 'EXPERT_COMPTABLE'],
        stagiaire: ['STAGIAIRE']
    };
    return roleGroups[group]?.includes(userRole) || false;
};

// Helper pour vérifier si un utilisateur a une permission spécifique
export const hasPermission = (permission) => {
    try {
        const user = JSON.parse(localStorage.getItem('userConnectedProfile') || localStorage.getItem('user') || '{}');
        if (user && user.permissions && Array.isArray(user.permissions)) {
            return user.permissions.some(p => p.libelle === permission);
        }
        return false;
    } catch (error) {
        console.error('Erreur lors de la vérification des permissions:', error);
        return false;
    }
};

// Helper pour obtenir le rôle de l'utilisateur
export const getUserRole = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user?.role?.libelle || '';
    } catch (error) {
        console.error('Erreur lors de la récupération du rôle:', error);
        return '';
    }
};

// Helper pour vérifier si l'utilisateur peut accéder à une route
export const canAccessRoute = (routeMeta) => {
    if (!routeMeta || (!routeMeta.requiresRole && !routeMeta.requiresPermission && !routeMeta.requiresRoleGroup)) {
        // Pas de restriction, accès autorisé
        return true;
    }

    const userRole = getUserRole();
    
    // Vérification par groupe de rôles
    if (routeMeta.requiresRoleGroup) {
        const roleGroups = Array.isArray(routeMeta.requiresRoleGroup) 
            ? routeMeta.requiresRoleGroup 
            : [routeMeta.requiresRoleGroup];
        
        const hasAccess = roleGroups.some(group => isInRoleGroup(userRole, group));
        if (!hasAccess) return false;
    }
    
    // Vérification par rôle spécifique
    if (routeMeta.requiresRole) {
        const requiredRoles = Array.isArray(routeMeta.requiresRole) 
            ? routeMeta.requiresRole 
            : [routeMeta.requiresRole];
        
        if (!requiredRoles.includes(userRole)) return false;
    }
    
    // Vérification par permission
    if (routeMeta.requiresPermission) {
        const requiredPermissions = Array.isArray(routeMeta.requiresPermission) 
            ? routeMeta.requiresPermission 
            : [routeMeta.requiresPermission];
        
        const hasAllPermissions = requiredPermissions.every(permission => hasPermission(permission));
        if (!hasAllPermissions) return false;
    }
    
    return true;
};

/**
 * Aligné sur Users.getStatutMaintien() (backend) : A_JOUR si dateExpiration >= aujourd'hui (jour calendaire).
 */
export const isMaintienAJourFromUser = (user) => {
    if (!user || user.dateExpiration == null || user.dateExpiration === '') {
        return false;
    }
    const raw = user.dateExpiration;
    let exp;
    if (typeof raw === 'string') {
        const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            exp = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10), 23, 59, 59, 999);
        } else {
            exp = new Date(raw);
        }
    } else {
        exp = new Date(raw);
    }
    if (Number.isNaN(exp.getTime())) {
        return false;
    }
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    const expDay = new Date(exp);
    expDay.setHours(0, 0, 0, 0);
    return expDay.getTime() >= startToday.getTime();
};

/**
 * Maintien société (personne morale) : aligné sur l'usage de statutMaintien / dateExpiration côté liste admin.
 */
export const isSocieteMaintienAJour = (s) => {
    if (!s) return false;
    const st = s.statutMaintien;
    if (st === 'A_JOUR' || st === 'a_jour') return true;
    if (st === 'EXPIRED' || st === 'EXPIRE' || st === 'expired') return false;
    return isMaintienAJourFromUser({ dateExpiration: s.dateExpiration });
};

/** Routes expert encore accessibles quand le maintien personnel n'est pas à jour (aligné sur le garde dans main.js). */
export const EXPERT_MAINTIEN_ROUTE_WHITELIST = new Set([
    'mesMaintien',
    'maintienPersonnelProcess',
    'renouvellementSociete',
    'mesSociete',
    'addSociete',
    'message',
    'piecesAdministratives'
]);

export default {
    middleware: async ({ to, store, next }) => {
        const requiredRole = to.meta.requiresRole;
        const user = store.getters['connexion/user'];
    
        if (!user) {
            // Ajoute un délai avant la redirection
            setTimeout(() => {
                next({ name: 'connexion' });
            }, 500);  // délai de 500ms pour éviter les redirections simultanées
        } else {
            const utili = JSON.parse(localStorage.getItem('user'));
    
            if (requiredRole && utili.role.libelle !== requiredRole) {
                // Ajoute un délai avant la redirection
                setTimeout(() => {
                    next({ name: 'profil' });
                }, 500);
            } else {
                next();
            }
        }
    }, 
    interceptor(error, store, router) {
        const { response, config } = error;
        const token = Vue.$cookies.get('OECCAtoken');

        // Vérifie si aucun token n'est présent
        if (!token) {
            router.replace({ name: 'connexion' });
            return Promise.reject(error);
        }

        if (!response) return Promise.reject(error);

        const { status } = response;

        if (status === 403) {
            return store.dispatch("connexion/logout").then(() =>
                router.replace({
                    name: 'connexion',
                    query: {
                        redirect: (router.currentRoute || {}).fullPath
                    }
                }).then(() => { })
            );
        }

        return Promise.reject(response);
    },
    // Export des helpers pour utilisation dans les composants
    isInRoleGroup,
    hasPermission,
    getUserRole,
    canAccessRoute,
    isMaintienAJourFromUser,
    isSocieteMaintienAJour,
    EXPERT_MAINTIEN_ROUTE_WHITELIST
};

